import React, { useState, useEffect } from 'react';
import { ProductFormPopupStandalone } from './WidgetTypes/ProductFormPopup';
import { database } from '../firebaseConfig';
import { ref, onValue, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import ImageUploadSection from './WidgetEditComponents/ImageUploadSection';
import ButtonEffectsSelector from './WidgetEditComponents/ButtonEffectsSelector';
import { getButtonEffectStyles } from './utils/buttonEffectsUtils';
import './PopupStylePreview.css';

/**
 * PopupStylePreview - Komponente für das Styling von Product Form Popups
 * 
 * Diese Komponente ermöglicht es, Popup-Styling zu bearbeiten und live zu testen,
 * ohne dass das Popup als normales Widget im Page Builder erscheint.
 * 
 * Updated: Fixed defaultStyling references - now uses localStyling state
 */
const PopupStylePreview = ({ 
    popupStyling = {}, 
    onStyleChange,
    products = [],
    selectedProductId = '', // Aktuell ausgewähltes Produkt vom Widget
    pageId = '' // Page ID für pro-Page Styling
}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    
    // Default styling values
    const defaultStyling = {
        backgroundColor: '#ffffff',
        overlayColor: '#ffffff', // Fester weißer Hintergrund als Standard
        overlayOpacity: 100, // Opacity in Prozent (0-100)
        overlayBlur: 0, // Blur-Effekt in px (0-20)
        borderRadius: 20,
        padding: 25,
        paddingTop: 5, // Separater oberer Abstand - minimal als Standard
        modalWidth: 600, // Modal-Breite in px
        modalHeight: 'auto', // Modal-Höhe (auto, fixed)
        // Logo/Image
        headerImage: '', // URL für Header-Bild/Logo
        headerImageSize: 80, // Größe des Header-Bildes in px
        headerImageShape: 'rounded', // Form: circle, rounded, square
        // Header styling
        headerTitleColor: '#1e293b',
        headerTitleSize: 32,
        headerSubtitleColor: '#64748b',
        headerSubtitleSize: 16,
        // Custom text content
        headerTitle: 'Get Free Access',
        headerSubtitle: '',
        // Text styling
        textColor: '#374151',
        labelColor: '#4b5563',
        // Field styling
        fieldBackgroundColor: '#f8fafc',
        fieldBorderColor: '#e2e8f0',
        fieldTextColor: '#1f2937',
        // Button styling
        submitButtonColor: '#667eea',
        submitButtonTextColor: '#ffffff',
        buttonText: 'Get Free Access Now',
        buttonEffect: 'none', // Button-Effekt ID
        buttonIcon: '', // Icon neben Button-Text (optional)
        buttonIconInvert: false, // Icon invertieren (für weiße Icons)
        // Footer styling
        footerTextColor: '#9ca3af',
        footerText: '🔒 Your information is secure and will never be shared.',
        // Styling mode
        stylingMode: 'default'
    };

    // Local state für Live-Updates
    const [localStyling, setLocalStyling] = useState(defaultStyling);

    // Load page-specific popup styling when pageId changes
    useEffect(() => {
        if (!pageId) {
            setLocalStyling(defaultStyling);
            return;
        }

        const auth = getAuth();
        const user = auth.currentUser;
        
        if (!user) return;

        // Load styling for this specific page - all popups on this page use same design
        const stylingRef = ref(database, `pages/${pageId}/popupStyling`);
        
        const unsubscribe = onValue(stylingRef, (snapshot) => {
            const pageStyling = snapshot.val();
            
            if (pageStyling) {
                console.log('🎨 PopupStylePreview: Loaded page styling for page:', pageId, pageStyling);
                setLocalStyling({
            ...defaultStyling,
                    ...pageStyling
                });
            } else {
                console.log('🎨 PopupStylePreview: No custom styling found for page:', pageId);
                setLocalStyling(defaultStyling);
            }
        });

        return () => unsubscribe();
    }, [pageId]);

    // Handle logo upload (compatible with ImageUploadSection)
    const uploadImage = (file, fieldName, setPreview, setUploadingState) => {
        if (!file || file.size > 2 * 1024 * 1024) {
            alert('File is too big (max. 2MB).');
            return;
        }

        setUploadingState(true);
        const storage = getStorage();
        const auth = getAuth();
        const userId = auth.currentUser?.uid;
        
        if (!userId) {
            alert('Please log in to upload images.');
            setUploadingState(false);
            return;
        }

        const imageRef = storageRef(storage, `users/${userId}/popup-logos/${fieldName}_${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(imageRef, file);

        uploadTask.on('state_changed', 
            (snapshot) => {
                // Optional: Upload-Fortschritt anzeigen
            }, 
            (error) => {
                console.error('Fehler beim Hochladen:', error);
                setUploadingState(false);
                alert('Upload failed: ' + error.message);
            }, 
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    handleStyleChange(fieldName, downloadURL);
                    setPreview(downloadURL);
                    setUploadingState(false);
                    console.log('✅ Logo uploaded successfully:', downloadURL);
                });
            }
        );
    };

    // Filter für kostenlose digitale Produkte
    const freeDigitalProducts = products.filter(product => 
        (product.type === 'digital-product' || product.type === 'online-course') &&
        (product.price === 0 || product.price === '0')
    );

    // Handle styling changes with live updates and per-product saving
    const handleStyleChange = async (field, value) => {
        const updatedStyling = {
            ...localStyling,
            [field]: value
        };
        
        setLocalStyling(updatedStyling);
        
        // Send live update to open popup
        const event = new CustomEvent('updatePopupStyling', {
            detail: { styling: updatedStyling }
        });
        window.dispatchEvent(event);
        
        // Save styling for this specific page - all popups on this page use same design
        if (pageId) {
            const auth = getAuth();
            const user = auth.currentUser;
            
            if (user) {
                try {
                    const stylingRef = ref(database, `pages/${pageId}/popupStyling`);
                    await set(stylingRef, updatedStyling);
                    console.log('✅ PopupStylePreview: Page styling saved for page:', pageId);
                } catch (error) {
                    console.error('❌ PopupStylePreview: Error saving page styling:', error);
                }
            }
        }
        
        if (onStyleChange) {
            onStyleChange(updatedStyling);
        }
    };

    const handlePreview = () => {
        if (selectedProductId) {
            // Show popup with currently selected product from widget
            const event = new CustomEvent('triggerProductFormPopup', {
                detail: { 
                    productId: selectedProductId,
                    trigger: 'popupStylePreview',
                    customStyling: localStyling
                }
            });
            window.dispatchEvent(event);
        } else {
            // Show test popup with sample data if no product selected
            const event = new CustomEvent('testFormPopup', {
                detail: { 
                    productTitle: 'Sample Digital Product',
                    trigger: 'popupStylePreview'
                }
            });
            window.dispatchEvent(event);
        }
    };

    return (
        <div className="popup-style-preview">
            <div className="widget-trenner">
                <h3>Popup Settings</h3>
                <hr style={{ marginBottom: '15px' }} className="hrrrr" />
            </div>

            {/* Control Buttons */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '12px',
                marginBottom: '24px'
            }}>
                {/* Preview Button */}
                <button
                    type="button"
                    onClick={handlePreview}
                    className="popup-preview-button"
                >
                    <span>👁️</span>
                    Preview Popup
                </button>

                {/* Reset Button */}
                <button
                    type="button"
                    onClick={() => {
                        const confirmReset = window.confirm(
                            '⚠️ Reset Popup Style\n\nThis will reset all popup styling to default values.\n\nAre you sure you want to continue?'
                        );
                        
                        if (confirmReset) {
                            // Reset to default styling
                            setLocalStyling(defaultStyling);
                            
                            // Send live update
                            const event = new CustomEvent('updatePopupStyling', {
                                detail: { styling: defaultStyling }
                            });
                            window.dispatchEvent(event);
                            
                            // Save to database
                            if (pageId) {
                                const auth = getAuth();
                                const user = auth.currentUser;
                                if (user) {
                                    const stylingRef = ref(database, `pages/${pageId}/popupStyling`);
                                    set(stylingRef, defaultStyling);
                                }
                            }
                            
                            if (onStyleChange) {
                                onStyleChange(defaultStyling);
                            }
                            
                            // Success feedback
                            setTimeout(() => {
                                alert('✅ Popup style has been reset to default values!');
                            }, 100);
                        }
                    }}
                    style={{
                        padding: '14px 16px',
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05))',
                        color: '#dc2626',
                        border: '2px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        minWidth: '120px'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.08))';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05))';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                    <span>🔄</span>
                    Reset
                </button>
            </div>

            {/* Logo/Header Image - Ganz oben */}
            <div className="widget-trenner">
                <h3>Header Logo</h3>
                <hr className="hrrrr" />
            </div>

            <ImageUploadSection
                label="Header Logo/Image"
                fieldName="headerImage"
                currentImage={localStyling.headerImage}
                onUpload={uploadImage}
                isUploading={isUploadingLogo}
                setIsUploading={setIsUploadingLogo}
            />

            {/* Logo Settings - nur wenn Image vorhanden */}
            {localStyling.headerImage && (
                <>
                    {/* Logo Size */}
                    <div className="input-group" style={{ marginTop: '16px' }}>
                        <label className="widget-label">
                            <img src="/img/element.svg" alt="" className="label-icon" />
                            Logo Size
                        </label>
                        <div className="popup-range-container">
                            <input 
                                type="range"
                                min="40"
                                max="300"
                                value={localStyling.headerImageSize || 80}
                                onChange={(e) => handleStyleChange('headerImageSize', parseInt(e.target.value))}
                                className="popup-range-slider"
                            />
                            <div className="popup-range-value">{localStyling.headerImageSize || 80}px</div>
                        </div>
                    </div>

                    {/* Logo Shape */}
                    <div className="input-group" style={{ marginTop: '16px' }}>
                        <label className="widget-label">
                            <img src="/img/element.svg" alt="" className="label-icon" />
                            Logo Shape
                        </label>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '8px',
                            marginTop: '8px'
                        }}>
                            {[
                                { 
                                    value: 'square', 
                                    name: 'Square', 
                                    preview: '⬜',
                                    borderRadius: '0px'
                                },
                                { 
                                    value: 'rounded', 
                                    name: 'Rounded', 
                                    preview: '▢',
                                    borderRadius: '8px'
                                },
                                { 
                                    value: 'circle', 
                                    name: 'Circle', 
                                    preview: '⭕',
                                    borderRadius: '50%'
                                }
                            ].map(shape => (
                                <div
                                    key={shape.value}
                                    onClick={() => handleStyleChange('headerImageShape', shape.value)}
                style={{
                                        padding: '12px',
                                        borderRadius: '6px',
                    cursor: 'pointer',
                                        border: localStyling.headerImageShape === shape.value || (!localStyling.headerImageShape && shape.value === 'rounded') ? '2px solid #667eea' : '2px solid #e2e8f0',
                                        background: localStyling.headerImageShape === shape.value || (!localStyling.headerImageShape && shape.value === 'rounded') ? 'rgba(102, 126, 234, 0.1)' : '#ffffff',
                                        transition: 'all 0.2s ease',
                                        textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                                        if (localStyling.headerImageShape !== shape.value && !(shape.value === 'rounded' && !localStyling.headerImageShape)) {
                                            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                        }
                }}
                onMouseLeave={(e) => {
                                        if (localStyling.headerImageShape !== shape.value && !(shape.value === 'rounded' && !localStyling.headerImageShape)) {
                                            e.currentTarget.style.background = '#ffffff';
                                        }
                                    }}
                                >
                                    {/* Shape Preview */}
                                    <div style={{
                                        width: '30px',
                                        height: '30px',
                                        margin: '0 auto 8px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        borderRadius: shape.borderRadius,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        color: 'white'
                                    }}>
                                        📷
                                    </div>
                                    <div style={{
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: localStyling.headerImageShape === shape.value || (!localStyling.headerImageShape && shape.value === 'rounded') ? '#667eea' : '#64748b'
                                    }}>
                                        {shape.name}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Style Presets & Modes */}
            <div className="widget-trenner" style={{ marginTop: '24px' }}>
                <h3>Style Presets & Modes</h3>
                <hr className="hrrrr" />
            </div>

            {/* Combined Style Presets */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label style={{ marginBottom: '12px' }}>
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Choose Your Style
                    </label>
                    
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '12px',
                        marginBottom: '24px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        padding: '4px'
                    }}>
                        {[
                            {
                                name: 'Clean White',
                                description: 'Professional & clean',
                                config: {
                                    stylingMode: 'default',
                                    backgroundColor: '#ffffff',
                                    overlayColor: '#ffffff',
                                    overlayOpacity: 100,
                                    overlayBlur: 0,
                                    headerTitleColor: '#1e293b',
                                    headerSubtitleColor: '#64748b',
                                    submitButtonColor: '#3b82f6',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#f8fafc',
                                    fieldBorderColor: '#e2e8f0',
                                    borderRadius: 16
                                },
                                gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                textColor: '#1e293b'
                            },
                            {
                                name: 'Midnight Dark',
                                description: 'Elegant dark theme',
                                config: {
                                    stylingMode: 'dark',
                                    backgroundColor: '#0f172a',
                                    overlayColor: 'rgba(0, 0, 0, 0.95)',
                                    overlayOpacity: 95,
                                    overlayBlur: 4,
                                    headerTitleColor: '#f1f5f9',
                                    headerSubtitleColor: '#cbd5e1',
                                    submitButtonColor: '#6366f1',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#1e293b',
                                    fieldBorderColor: '#374151',
                                    fieldTextColor: '#f1f5f9',
                                    labelColor: '#cbd5e1',
                                    borderRadius: 20
                                },
                                gradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                textColor: '#f1f5f9'
                            },
                            {
                                name: 'Ocean Glass',
                                description: 'Modern glassmorphism',
                                config: {
                                    stylingMode: 'glass',
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                    overlayColor: 'rgba(59, 130, 246, 0.2)',
                                    overlayOpacity: 20,
                                    overlayBlur: 16,
                                    headerTitleColor: '#1e40af',
                                    headerSubtitleColor: '#3730a3',
                                    submitButtonColor: '#2563eb',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    fieldBorderColor: 'rgba(59, 130, 246, 0.3)',
                                    fieldTextColor: '#1e40af',
                                    labelColor: '#1e40af',
                                    borderRadius: 24
                                },
                                gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(147, 51, 234, 0.2) 100%)',
                                textColor: '#1e40af'
                            },
                            {
                                name: 'Sunset Gradient',
                                description: 'Warm gradient theme',
                                config: {
                                    stylingMode: 'gradient',
                                    backgroundColor: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%)',
                                    overlayColor: 'rgba(0, 0, 0, 0.4)',
                                    overlayOpacity: 40,
                                    overlayBlur: 8,
                                    headerTitleColor: '#ffffff',
                                    headerSubtitleColor: '#fef3c7',
                                    submitButtonColor: '#ffffff',
                                    submitButtonTextColor: '#dc2626',
                                    fieldBackgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    fieldBorderColor: 'rgba(255, 255, 255, 0.4)',
                                    fieldTextColor: '#ffffff',
                                    labelColor: '#fef3c7',
                                    footerTextColor: '#fde68a',
                                    borderRadius: 20
                                },
                                gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%)',
                                textColor: '#ffffff'
                            },
                            {
                                name: 'Forest Green',
                                description: 'Nature-inspired theme',
                                config: {
                                    stylingMode: 'default',
                                    backgroundColor: '#ffffff',
                                    overlayColor: 'rgba(34, 197, 94, 0.15)',
                                    overlayOpacity: 15,
                                    overlayBlur: 6,
                                    headerTitleColor: '#14532d',
                                    headerSubtitleColor: '#166534',
                                    submitButtonColor: '#16a34a',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#f0fdf4',
                                    fieldBorderColor: '#bbf7d0',
                                    fieldTextColor: '#14532d',
                                    labelColor: '#166534',
                                    footerTextColor: '#4ade80',
                                    borderRadius: 16
                                },
                                gradient: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                textColor: '#14532d'
                            },
                            {
                                name: 'Purple Luxury',
                                description: 'Premium luxury feel',
                                config: {
                                    stylingMode: 'default',
                                    backgroundColor: '#ffffff',
                                    overlayColor: 'rgba(147, 51, 234, 0.12)',
                                    overlayOpacity: 12,
                                    overlayBlur: 10,
                                    headerTitleColor: '#581c87',
                                    headerSubtitleColor: '#7c3aed',
                                    submitButtonColor: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#faf5ff',
                                    fieldBorderColor: '#d8b4fe',
                                    fieldTextColor: '#581c87',
                                    labelColor: '#7c3aed',
                                    footerTextColor: '#a855f7',
                                    borderRadius: 20,
                                    modalWidth: 650
                                },
                                gradient: 'linear-gradient(135deg, #faf5ff 0%, #e9d5ff 100%)',
                                textColor: '#581c87'
                            },
                            {
                                name: 'Compact Mini',
                                description: 'Small & efficient',
                                config: {
                                    stylingMode: 'minimal',
                                    backgroundColor: '#ffffff',
                                    overlayColor: 'rgba(0, 0, 0, 0.6)',
                                    overlayOpacity: 60,
                                    overlayBlur: 3,
                                    headerTitleColor: '#1f2937',
                                    headerSubtitleColor: '#6b7280',
                                    submitButtonColor: '#059669',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#ffffff',
                                    fieldBorderColor: '#d1d5db',
                                    fieldTextColor: '#1f2937',
                                    labelColor: '#374151',
                                    borderRadius: 8,
                                    padding: 25,
                                    modalWidth: 400
                                },
                                gradient: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                                textColor: '#1f2937'
                            },
                            {
                                name: 'Wide Professional',
                                description: 'Spacious & corporate',
                                config: {
                                    stylingMode: 'default',
                                    backgroundColor: '#ffffff',
                                    overlayColor: 'rgba(15, 23, 42, 0.8)',
                                    overlayOpacity: 80,
                                    overlayBlur: 2,
                                    headerTitleColor: '#0f172a',
                                    headerSubtitleColor: '#475569',
                                    submitButtonColor: '#0f172a',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#f8fafc',
                                    fieldBorderColor: '#cbd5e1',
                                    fieldTextColor: '#0f172a',
                                    labelColor: '#475569',
                                    borderRadius: 12,
                                    padding: 35,
                                    paddingTop: 40, // Mehr Platz oben für Professional
                                    modalWidth: 800
                                },
                                gradient: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                textColor: '#0f172a'
                            },
                            {
                                name: 'Neon Cyber',
                                description: 'Futuristic neon style',
                                config: {
                                    stylingMode: 'dark',
                                    backgroundColor: '#0a0a0a',
                                    overlayColor: 'rgba(0, 0, 0, 0.95)',
                                    overlayOpacity: 95,
                                    overlayBlur: 5,
                                    headerTitleColor: '#00ff88',
                                    headerSubtitleColor: '#00ccff',
                                    submitButtonColor: 'linear-gradient(135deg, #00ff88 0%, #00ccff 100%)',
                                    submitButtonTextColor: '#000000',
                                    fieldBackgroundColor: '#1a1a1a',
                                    fieldBorderColor: '#00ff88',
                                    fieldTextColor: '#00ff88',
                                    labelColor: '#00ccff',
                                    footerTextColor: '#888888',
                                    borderRadius: 4,
                                    modalWidth: 500
                                },
                                gradient: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
                                textColor: '#00ff88'
                            },
                            {
                                name: 'Rose Gold',
                                description: 'Elegant rose theme',
                                config: {
                                    stylingMode: 'default',
                                    backgroundColor: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
                                    overlayColor: 'rgba(244, 63, 94, 0.1)',
                                    overlayOpacity: 10,
                                    overlayBlur: 12,
                                    headerTitleColor: '#881337',
                                    headerSubtitleColor: '#be185d',
                                    submitButtonColor: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#fdf2f8',
                                    fieldBorderColor: '#f9a8d4',
                                    fieldTextColor: '#881337',
                                    labelColor: '#be185d',
                                    footerTextColor: '#f43f5e',
                                    borderRadius: 24,
                                    padding: 30,
                                    modalWidth: 550
                                },
                                gradient: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 50%, #fbcfe8 100%)',
                                textColor: '#881337'
                            },
                            {
                                name: 'Retro Gaming',
                                description: '8-bit inspired design',
                                config: {
                                    stylingMode: 'default',
                                    backgroundColor: '#1e1b4b',
                                    overlayColor: 'rgba(30, 27, 75, 0.9)',
                                    overlayOpacity: 90,
                                    overlayBlur: 0,
                                    headerTitleColor: '#fbbf24',
                                    headerSubtitleColor: '#a78bfa',
                                    submitButtonColor: '#fbbf24',
                                    submitButtonTextColor: '#1e1b4b',
                                    fieldBackgroundColor: '#312e81',
                                    fieldBorderColor: '#fbbf24',
                                    fieldTextColor: '#fbbf24',
                                    labelColor: '#a78bfa',
                                    footerTextColor: '#c4b5fd',
                                    borderRadius: 0,
                                    padding: 25,
                                    paddingTop: 10, // Minimaler Top-Abstand für Gaming/Seamless
                                    modalWidth: 480
                                },
                                gradient: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                                textColor: '#fbbf24'
                            },
                            {
                                name: 'Seamless White',
                                description: 'Blends with background',
                                config: {
                                    stylingMode: 'seamless',
                                    backgroundColor: '#ffffff',
                                    overlayColor: '#ffffff',
                                    overlayOpacity: 100,
                                    overlayBlur: 0,
                                    headerTitleColor: '#1e293b',
                                    headerSubtitleColor: '#64748b',
                                    submitButtonColor: '#667eea',
                                    submitButtonTextColor: '#ffffff',
                                    fieldBackgroundColor: '#f8fafc',
                                    fieldBorderColor: '#e2e8f0',
                                    fieldTextColor: '#1f2937',
                                    labelColor: '#4b5563',
                                    footerTextColor: '#9ca3af',
                                    borderRadius: 0,
                                    padding: 25,
                                    paddingTop: 10, // Minimaler Top-Abstand für Gaming/Seamless
                                    modalWidth: 600,
                                    boxShadow: 'none' // Kein Schatten für seamless effect
                                },
                                gradient: 'linear-gradient(135deg, #ffffff 0%, #ffffff 100%)',
                                textColor: '#1e293b'
                            },
                            // Original Styling Modes as presets
                            {
                                name: 'Glass Effect',
                                description: 'Modern glassmorphism',
                                config: {
                                    stylingMode: 'glass',
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    borderRadius: 24,
                                    overlayColor: 'rgba(0, 0, 0, 0.4)',
                                    overlayOpacity: 40,
                                    overlayBlur: 15,
                                    modalWidth: 600
                                },
                                gradient: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
                                textColor: '#1f2937'
                            },
                            {
                                name: 'Transparent',
                                description: 'Frameless design',
                                config: {
                                    stylingMode: 'transparent',
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    borderRadius: 0,
                                    overlayColor: 'rgba(0, 0, 0, 0.3)',
                                    overlayOpacity: 30,
                                    overlayBlur: 5,
                                    modalWidth: 600
                                },
                                gradient: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                textColor: '#1f2937'
                            },
                            {
                                name: 'Minimal Clean',
                                description: 'Simple & elegant',
                                config: {
                                    stylingMode: 'minimal',
                                    backgroundColor: '#ffffff',
                                    borderRadius: 8,
                                    overlayColor: 'rgba(0, 0, 0, 0.5)',
                                    overlayOpacity: 50,
                                    overlayBlur: 2,
                                    modalWidth: 600
                                },
                                gradient: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                                textColor: '#1f2937'
                            }
                        ].map((preset, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    // Apply preset configuration
                                    const updatedStyling = {
                                        ...localStyling,
                                        ...preset.config
                                    };
                                    setLocalStyling(updatedStyling);
                                    
                                    // Send live update
                                    const event = new CustomEvent('updatePopupStyling', {
                                        detail: { styling: updatedStyling }
                                    });
                                    window.dispatchEvent(event);
                                    
                                    // Save to database
                                    if (pageId) {
                                        const auth = getAuth();
                                        const user = auth.currentUser;
                                        if (user) {
                                            const stylingRef = ref(database, `pages/${pageId}/popupStyling`);
                                            set(stylingRef, updatedStyling);
                                        }
                                    }
                                    
                                    if (onStyleChange) {
                                        onStyleChange(updatedStyling);
                                    }
                                }}
                                style={{
                                    padding: '16px',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    border: '2px solid #e2e8f0',
                                    background: preset.gradient,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    textAlign: 'center',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                                }}
                            >
                                {/* Preset Preview */}
                                <div style={{
                                    width: '100%',
                                    height: '60px',
                                    borderRadius: '8px',
                                    background: preset.config.backgroundColor?.includes('gradient') ? 
                                               preset.config.backgroundColor : 
                                               `linear-gradient(135deg, ${preset.config.backgroundColor || '#ffffff'} 0%, ${preset.config.fieldBackgroundColor || '#f8fafc'} 100%)`,
                                    marginBottom: '12px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '8px',
                                    fontWeight: '600',
                                    color: preset.textColor,
                                    position: 'relative',
                                    border: `1px solid ${preset.config.fieldBorderColor || '#e2e8f0'}`,
                                    overflow: 'hidden'
                                }}>
                                    {/* Simulated Header */}
                                    <div style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        color: preset.config.headerTitleColor,
                                        marginBottom: '2px'
                                    }}>
                                        HEADER
                                    </div>
                                    
                                    {/* Simulated Field */}
                                    <div style={{
                                        width: '70%',
                                        height: '12px',
                                        background: preset.config.fieldBackgroundColor,
                                        borderRadius: '2px',
                                        border: `1px solid ${preset.config.fieldBorderColor}`,
                                        marginBottom: '4px'
                                    }}></div>
                                    
                                    {/* Simulated Button */}
                                    <div style={{
                                        width: '60%',
                                        height: '10px',
                                        background: preset.config.submitButtonColor?.includes('gradient') ? 
                                                   preset.config.submitButtonColor :
                                                   preset.config.submitButtonColor,
                                        borderRadius: '2px',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}></div>
                                    
                                    {/* Blur Effect Indicator */}
                                    {preset.config.overlayBlur > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'radial-gradient(circle, transparent 30%, rgba(255,255,255,0.1) 70%)',
                                            backdropFilter: `blur(${Math.min(preset.config.overlayBlur / 4, 3)}px)`
                                        }} />
                                    )}
                                </div>
                                
                                {/* Preset Info */}
                                <div>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: preset.textColor,
                                        marginBottom: '4px',
                                        textShadow: preset.config.backgroundColor?.includes('gradient') ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                    }}>
                                        {preset.name}
                                    </div>
                                    <div style={{
                                        fontSize: '10px',
                                        color: preset.config.headerSubtitleColor || '#64748b',
                                        lineHeight: '1.3',
                                        textShadow: preset.config.backgroundColor?.includes('gradient') ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                    }}>
                                        {preset.description}
                                    </div>
                                </div>
                                
                                {/* Shimmer Effect */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                    animation: 'shimmer 3s infinite',
                                    borderRadius: '12px'
                                }} />
                            </div>
                        ))}
                    </div>
                    
                    <div style={{
                        padding: '12px',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.03))',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        fontSize: '12px',
                        color: '#64748b',
                        textAlign: 'center'
                    }}>
                        💡 Click a preset to apply, then fine-tune with the controls below
                    </div>
                </div>
            </div>

            {/* Background Color */}
            <div className="popup-style-section">
            <div className="input-group">
                    <label htmlFor="backgroundColor">
                        <img src="/img/background.svg" alt="" className="label-icon" />
                        Background Color
                    </label>
                    <div className="popup-color-picker">
                    <input 
                        id="backgroundColor"
                        type="color" 
                            value={localStyling.backgroundColor}
                        onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                    />
                    <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.backgroundColor }}
                    ></div>
                        <div className="popup-color-value">
                            {localStyling.backgroundColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Border Radius */}
            <div className="popup-style-section">
            <div className="input-group">
                    <label htmlFor="borderRadius">
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Border Radius
                    </label>
                    <div className="popup-range-container">
                <input 
                    id="borderRadius"
                    type="range"
                    min="0"
                    max="50"
                            value={localStyling.borderRadius}
                    onChange={(e) => handleStyleChange('borderRadius', parseInt(e.target.value))}
                            className="popup-range-slider"
                />
                        <div className="popup-range-value">{localStyling.borderRadius}px</div>
                    </div>
                </div>
            </div>

            {/* Padding */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="padding">
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Side Padding
                    </label>
                    <div className="popup-range-container">
                        <input 
                            id="padding"
                            type="range"
                            min="15"
                            max="60"
                            value={localStyling.padding}
                            onChange={(e) => handleStyleChange('padding', parseInt(e.target.value))}
                            className="popup-range-slider"
                        />
                        <div className="popup-range-value">{localStyling.padding}px</div>
                    </div>
                </div>
            </div>

            {/* Padding Top */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="paddingTop">
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Top Padding
                    </label>
                    <div className="popup-range-container">
                        <input 
                            id="paddingTop"
                            type="range"
                            min="5"
                            max="50"
                            value={localStyling.paddingTop || 5}
                            onChange={(e) => handleStyleChange('paddingTop', parseInt(e.target.value))}
                            className="popup-range-slider"
                        />
                        <div className="popup-range-value">{localStyling.paddingTop || 5}px</div>
                    </div>
                    <small style={{ color: '#6b7280', fontSize: '11px', marginTop: '4px' }}>
                        Controls the space at the top of the popup content
                    </small>
                </div>
            </div>

            {/* Modal Width */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="modalWidth">
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Modal Width
                    </label>
                    <div className="popup-range-container">
                        <input 
                            id="modalWidth"
                            type="range"
                            min="350"
                            max="900"
                            value={localStyling.modalWidth || 600}
                            onChange={(e) => handleStyleChange('modalWidth', parseInt(e.target.value))}
                            className="popup-range-slider"
                        />
                        <div className="popup-range-value">{localStyling.modalWidth || 600}px</div>
                    </div>
                </div>
            </div>

            {/* Header Title Color */}
            <div className="popup-style-section">
            <div className="input-group">
                    <label htmlFor="headerTitleColor">
                        <img src="/img/text-fat.svg" alt="" className="label-icon" />
                        Header Title Color
                    </label>
                    <div className="popup-color-picker">
                    <input 
                        id="headerTitleColor"
                        type="color" 
                            value={localStyling.headerTitleColor}
                        onChange={(e) => handleStyleChange('headerTitleColor', e.target.value)}
                    />
                    <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.headerTitleColor }}
                    ></div>
                        <div className="popup-color-value">
                            {localStyling.headerTitleColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Header Title Size */}
            <div className="popup-style-section">
            <div className="input-group">
                    <label htmlFor="headerTitleSize">
                        <img src="/img/text-fat.svg" alt="" className="label-icon" />
                        Header Title Size
                    </label>
                    <div className="popup-range-container">
                <input 
                    id="headerTitleSize"
                    type="range"
                    min="16"
                    max="48"
                            value={localStyling.headerTitleSize}
                    onChange={(e) => handleStyleChange('headerTitleSize', parseInt(e.target.value))}
                            className="popup-range-slider"
                />
                        <div className="popup-range-value">{localStyling.headerTitleSize}px</div>
                    </div>
                </div>
            </div>

            {/* Button Color */}
            <div className="popup-style-section">
            <div className="input-group">
                    <label htmlFor="submitButtonColor">
                        <img src="/img/background.svg" alt="" className="label-icon" />
                        Submit Button Color
                    </label>
                    <div className="popup-color-picker">
                    <input 
                        id="submitButtonColor"
                        type="color" 
                            value={localStyling.submitButtonColor}
                        onChange={(e) => handleStyleChange('submitButtonColor', e.target.value)}
                    />
                    <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.submitButtonColor }}
                    ></div>
                        <div className="popup-color-value">
                            {localStyling.submitButtonColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Button Text Color */}
            <div className="popup-style-section">
            <div className="input-group">
                    <label htmlFor="submitButtonTextColor">
                        <img src="/img/text.svg" alt="" className="label-icon" />
                        Submit Button Text Color
                    </label>
                    <div className="popup-color-picker">
                    <input 
                        id="submitButtonTextColor"
                        type="color" 
                            value={localStyling.submitButtonTextColor}
                        onChange={(e) => handleStyleChange('submitButtonTextColor', e.target.value)}
                    />
                    <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.submitButtonTextColor }}
                    ></div>
                        <div className="popup-color-value">
                            {localStyling.submitButtonTextColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Button Effects */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label style={{ marginBottom: '12px' }}>
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Button Effects
                    </label>
                    
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        background: '#f8fafc'
                    }}>
                        <span style={{ 
                            fontSize: '13px', 
                            color: '#64748b',
                            flex: 1 
                        }}>
                            {localStyling.buttonEffect === 'none' ? 'No effect selected' : `Effect: ${localStyling.buttonEffect}`}
                        </span>
                        
                        <ButtonEffectsSelector
                            fieldName="buttonEffect"
                            currentEffect={localStyling.buttonEffect || 'none'}
                            onChange={(e) => handleStyleChange(e.target.name, e.target.value)}
                        />
                    </div>
                </div>
            </div>


            {/* Additional Color Options */}

            {/* Text Content Editing */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label style={{ marginBottom: '12px' }}>
                        <img src="/img/text-fat.svg" alt="" className="label-icon" />
                        Header Text Content
                    </label>
                    
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        {/* Title Text */}
                        <div>
                            <label htmlFor="headerTitle" style={{ fontSize: '12px', marginBottom: '6px', display: 'block' }}>
                                Main Title:
                            </label>
                            <input
                                id="headerTitle"
                                type="text"
                                placeholder="Get Free Access"
                                value={localStyling.headerTitle || ''}
                                onChange={(e) => handleStyleChange('headerTitle', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease',
                                    outline: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#667eea';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                        
                        {/* Subtitle Text */}
                        <div>
                            <label htmlFor="headerSubtitle" style={{ fontSize: '12px', marginBottom: '6px', display: 'block' }}>
                                Subtitle (optional):
                            </label>
                            <input
                                id="headerSubtitle"
                                type="text"
                                placeholder="Enter subtitle text..."
                                value={localStyling.headerSubtitle || ''}
                                onChange={(e) => handleStyleChange('headerSubtitle', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease',
                                    outline: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#667eea';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* Button Text */}
                        <div>
                            <label htmlFor="buttonText" style={{ fontSize: '12px', marginBottom: '6px', display: 'block' }}>
                                Button Text:
                            </label>
                            <input
                                id="buttonText"
                                type="text"
                                placeholder="Get Free Access Now"
                                value={localStyling.buttonText || ''}
                                onChange={(e) => handleStyleChange('buttonText', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease',
                                    outline: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#667eea';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* Button Icon Selection */}
                        <div>
                            <label style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                                Button Icon (optional):
                            </label>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gap: '8px',
                                marginBottom: '12px'
                            }}>
                                {[
                                    { value: '', name: 'None', icon: '⚪' },
                                    { value: 'key.png', name: 'Key', icon: '🔑' },
                                    { value: 'paperplane.svg', name: 'Send', icon: '✈️' },
                                    { value: 'right.svg', name: 'Right', icon: '➡️' },
                                    { value: 'upright.svg', name: 'Up Right', icon: '↗️' }
                                ].map(iconOption => (
                                    <div
                                        key={iconOption.value}
                                        onClick={() => handleStyleChange('buttonIcon', iconOption.value)}
                                        style={{
                                            padding: '12px 8px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            border: localStyling.buttonIcon === iconOption.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                                            background: localStyling.buttonIcon === iconOption.value ? 'rgba(102, 126, 234, 0.1)' : '#ffffff',
                                            transition: 'all 0.2s ease',
                                            textAlign: 'center',
                                            position: 'relative'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (localStyling.buttonIcon !== iconOption.value) {
                                                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (localStyling.buttonIcon !== iconOption.value) {
                                                e.currentTarget.style.background = '#ffffff';
                                            }
                                        }}
                                    >
                                        {/* Icon Preview */}
                                        <div style={{
                                            fontSize: '16px',
                                            marginBottom: '4px'
                                        }}>
                                            {iconOption.value ? (
                                                <img 
                                                    src={`/img/${iconOption.value}`}
                                                    alt={iconOption.name}
                                                    style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        filter: localStyling.buttonIconInvert ? 'invert(1)' : 'none'
                                                    }}
                                                />
                                            ) : (
                                                iconOption.icon
                                            )}
                                        </div>
                                        <div style={{
                                            fontSize: '9px',
                                            fontWeight: '600',
                                            color: localStyling.buttonIcon === iconOption.value ? '#667eea' : '#64748b'
                                        }}>
                                            {iconOption.name}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Icon Invert Toggle */}
                            {localStyling.buttonIcon && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    background: '#f8fafc',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <input
                                        type="checkbox"
                                        id="buttonIconInvert"
                                        checked={localStyling.buttonIconInvert || false}
                                        onChange={(e) => handleStyleChange('buttonIconInvert', e.target.checked)}
                                        style={{ marginRight: '4px' }}
                                    />
                                    <label htmlFor="buttonIconInvert" style={{ fontSize: '12px', color: '#64748b' }}>
                                        Invert icon color (for white icons)
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Footer Text */}
                        <div>
                            <label htmlFor="footerText" style={{ fontSize: '12px', marginBottom: '6px', display: 'block' }}>
                                Footer Text:
                            </label>
                            <input
                                id="footerText"
                                type="text"
                                placeholder="🔒 Your information is secure and will never be shared."
                                value={localStyling.footerText || ''}
                                onChange={(e) => handleStyleChange('footerText', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease',
                                    outline: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#667eea';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="widget-trenner" style={{ marginTop: '20px' }}>
                <h3>Additional Colors</h3>
                <hr className="hrrrr" />
            </div>

            {/* Overlay Color */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="overlayColor">
                        <img src="/img/background.svg" alt="" className="label-icon" />
                        Background Overlay Color
                    </label>
                    <div className="popup-color-picker">
                        <input 
                            id="overlayColor"
                            type="color" 
                            value={localStyling.overlayColor?.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+).*\)/, (match, r, g, b) => {
                                return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`;
                            }) || (localStyling.overlayColor?.startsWith('#') ? localStyling.overlayColor : '#ffffff')}
                            onChange={(e) => {
                                const hex = e.target.value;
                                const opacity = (localStyling.overlayOpacity || 100) / 100;
                                
                                if (opacity === 1) {
                                    // Full opacity - use solid color
                                    handleStyleChange('overlayColor', hex);
                                } else {
                                    // With transparency - use rgba
                                    const r = parseInt(hex.slice(1, 3), 16);
                                    const g = parseInt(hex.slice(3, 5), 16);
                                    const b = parseInt(hex.slice(5, 7), 16);
                                    const rgba = `rgba(${r}, ${g}, ${b}, ${opacity})`;
                                    handleStyleChange('overlayColor', rgba);
                                }
                            }}
                        />
                        <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.overlayColor }}
                        ></div>
                        <div className="popup-color-value" style={{ fontSize: '10px' }}>
                            {localStyling.overlayColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay Opacity */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="overlayOpacity">
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Background Overlay Opacity
                    </label>
                    <div className="popup-range-container">
                        <input 
                            id="overlayOpacity"
                            type="range"
                            min="0"
                            max="100"
                            value={localStyling.overlayOpacity || 100}
                            onChange={(e) => {
                                const opacity = parseInt(e.target.value);
                                const currentColor = localStyling.overlayColor;
                                
                                if (opacity === 100) {
                                    // Full opacity - convert to solid color
                                    const hex = currentColor?.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+).*\)/, (match, r, g, b) => {
                                        return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`;
                                    }) || currentColor;
                                    
                                    handleStyleChange('overlayOpacity', opacity);
                                    handleStyleChange('overlayColor', hex);
                                } else {
                                    // With transparency - convert to rgba
                                    const hex = currentColor?.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+).*\)/, (match, r, g, b) => {
                                        return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`;
                                    }) || currentColor || '#000000';
                                    
                                    const r = parseInt(hex.slice(1, 3), 16);
                                    const g = parseInt(hex.slice(3, 5), 16);
                                    const b = parseInt(hex.slice(5, 7), 16);
                                    const rgba = `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                                    
                                    handleStyleChange('overlayOpacity', opacity);
                                    handleStyleChange('overlayColor', rgba);
                                }
                            }}
                            className="popup-range-slider"
                        />
                        <div className="popup-range-value">{localStyling.overlayOpacity || 100}%</div>
                    </div>
                </div>
            </div>

            {/* Background Blur */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="overlayBlur">
                        <img src="/img/element.svg" alt="" className="label-icon" />
                        Background Blur Effect
                    </label>
                    <div className="popup-range-container">
                        <input 
                            id="overlayBlur"
                            type="range"
                            min="0"
                            max="20"
                            value={localStyling.overlayBlur || 0}
                            onChange={(e) => handleStyleChange('overlayBlur', parseInt(e.target.value))}
                            className="popup-range-slider"
                        />
                        <div className="popup-range-value">{localStyling.overlayBlur || 0}px</div>
                    </div>
                    <small style={{ color: '#6b7280', fontSize: '11px', marginTop: '4px' }}>
                        Blurs the content behind the popup (glassmorphism effect)
                    </small>
                </div>
            </div>

            {/* Text Colors */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="textColor">
                        <img src="/img/text.svg" alt="" className="label-icon" />
                        Body Text Color
                    </label>
                    <div className="popup-color-picker">
                        <input 
                            id="textColor"
                            type="color" 
                            value={localStyling.textColor}
                            onChange={(e) => handleStyleChange('textColor', e.target.value)}
                        />
                        <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.textColor }}
                        ></div>
                        <div className="popup-color-value">
                            {localStyling.textColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Label Color */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="labelColor">
                        <img src="/img/text.svg" alt="" className="label-icon" />
                        Label Color
                    </label>
                    <div className="popup-color-picker">
                        <input 
                            id="labelColor"
                            type="color" 
                            value={localStyling.labelColor}
                            onChange={(e) => handleStyleChange('labelColor', e.target.value)}
                        />
                        <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.labelColor }}
                        ></div>
                        <div className="popup-color-value">
                            {localStyling.labelColor}
                        </div>
                    </div>
                </div>
            </div>

            {/* Field Background Color */}
            <div className="popup-style-section">
                <div className="input-group">
                    <label htmlFor="fieldBackgroundColor">
                        <img src="/img/background.svg" alt="" className="label-icon" />
                        Field Background
                    </label>
                    <div className="popup-color-picker">
                        <input 
                            id="fieldBackgroundColor"
                            type="color" 
                            value={localStyling.fieldBackgroundColor}
                            onChange={(e) => handleStyleChange('fieldBackgroundColor', e.target.value)}
                        />
                        <div 
                            className="popup-color-display" 
                            style={{ backgroundColor: localStyling.fieldBackgroundColor }}
                        ></div>
                        <div className="popup-color-value">
                            {localStyling.fieldBackgroundColor}
                        </div>
                    </div>
                </div>
            </div>

            <div className="widget-trenner" style={{ marginTop: '20px' }}>
                <h3>Form Field Settings</h3>
                <hr className="hrrrr" />
            </div>

            <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#64748b',
                fontStyle: 'italic'
            }}>
                ℹ️ Form fields (name, email, etc.) are automatically configured from the selected product settings. They cannot be edited here to maintain consistency.
            </div>
        </div>
    );
};

export default PopupStylePreview;
